/**
 * LOD SELECTION FOR THE CAST — distance in, tier out, with hysteresis.
 *
 * ── Why this is a file and not two lines in the update loop ─────────────────
 *
 * Twenty-two players at LOD0 is not shippable and never was: the ladder exists
 * (roughly 42k / 20k / 9k / 4.6k triangles) and nothing was selecting from it.
 * The selection is separated out for three reasons that all cost real time when
 * they are inlined:
 *
 *   1. IT IS THE ONE THING A BENCHMARK HAS TO REPRODUCE EXACTLY. A perf number
 *      measured with a different band table is a number about a different game.
 *      `scripts/bench-cast.mjs` imports THESE bands.
 *   2. HYSTERESIS IS NOT OPTIONAL AND IS EASY TO FORGET. A player standing on a
 *      band edge with a camera that breathes 20 cm swaps tier every few frames;
 *      each swap is a geometry rebind and a visible pop of ~20k triangles. The
 *      band a player is IN is stickier than the band he would be assigned cold.
 *   3. IT IS PURE, SO IT IS TESTABLE. `castLod.test.ts` pins the switch
 *      distances; the alternative is proving LOD selection from screenshots.
 *
 * ── Where the numbers come from ────────────────────────────────────────────
 *
 * `assets/player-master/pipeline/export_runtime.py` writes its intended bands
 * into the export report, and they are the bands the ladder was DECIMATED for:
 *
 *     LOD0  0-6 m    menus / replays / close-ups
 *     LOD1  6-18 m   the gameplay camera sits at 17-20 m
 *     LOD2  18-38 m  far half of the pitch
 *     LOD3  38+ m    crowd distance
 *
 * They are reproduced here as the RUNTIME contract with one deliberate change:
 * the LOD0 band is widened to 8 m. The gameplay camera is at 17-20 m and a
 * replay/celebration camera comes in to ~6-7 m, and at exactly 6 m a 42k model
 * swapping to 20k mid-celebration is the one place the pop is watched. Nothing
 * else moves.
 *
 * A note the benchmark keeps having to re-learn: at the broadcast camera
 * (18 m up, 25 m back) NO player is inside 18 m — the nearest is ~21 m and the
 * far touchline is ~52 m. So a correct band table puts the whole squad on
 * LOD2/LOD3 in a broadcast shot, and the LOD0 tier is a close-up asset that a
 * match camera should essentially never ask for. That is the intended result,
 * not a bug in the table.
 */
import { CAST_LOD_LEVELS, type CastLodLevel } from './castContract';

export interface CastLodBand {
  readonly lod: CastLodLevel;
  /** Upper edge of the band, metres from the camera. */
  readonly maxM: number;
  readonly why: string;
}

export const CAST_LOD_BANDS: readonly CastLodBand[] = [
  { lod: 0, maxM: 8, why: 'close-ups, replays, celebration cameras' },
  { lod: 1, maxM: 18, why: 'the gameplay camera at 17-20 m sits on this edge' },
  { lod: 2, maxM: 38, why: 'far half of the pitch from a broadcast camera' },
  { lod: 3, maxM: Infinity, why: 'crowd distance' },
];

/**
 * How far past a band edge a player must travel before he changes tier.
 *
 * One metre, symmetric. The camera dolly moves ~0.4 m/s and players cross an
 * edge at up to 9 m/s, so this costs at most ~110 ms of "wrong" tier on a
 * sprint and removes the standing-still flicker entirely.
 */
export const CAST_LOD_HYSTERESIS_M = 1.0;

/** Cold selection: no previous tier, no hysteresis. */
export function castLodForDistance(distanceM: number): CastLodLevel {
  for (const band of CAST_LOD_BANDS) {
    if (distanceM < band.maxM) return band.lod;
  }
  return CAST_LOD_BANDS[CAST_LOD_BANDS.length - 1]!.lod;
}

/**
 * Sticky selection. `current` is what this player is drawing right now.
 *
 * The rule is one-sided per edge: to move to a COARSER tier you must be past
 * the edge by the margin; to move to a FINER tier you must be inside it by the
 * margin. A player who never leaves the dead band keeps the tier he has, which
 * is the entire point.
 */
export function castLodStep(distanceM: number, current: CastLodLevel): CastLodLevel {
  const cold = castLodForDistance(distanceM);
  if (cold === current) return current;
  if (cold > current) {
    // Moving away. The edge being crossed is the top of the CURRENT band.
    const edge = CAST_LOD_BANDS[current]!.maxM;
    return distanceM > edge + CAST_LOD_HYSTERESIS_M ? cold : current;
  }
  // Moving closer. The edge is the top of the band we would drop into.
  const edge = CAST_LOD_BANDS[cold]!.maxM;
  return distanceM < edge - CAST_LOD_HYSTERESIS_M ? cold : current;
}

/** The tier a character should be loaded at first, before anything is on screen. */
export const CAST_PRELOAD_LOD: CastLodLevel = 2;

/**
 * Which tiers are worth having in memory at all for a given quality tier.
 *
 * Loading four tiers of thirty-six characters is 570 MB of GLB. The match only
 * ever needs the tiers its camera can reach, and on `low` the whole LOD0 band is
 * unreachable because the replay cameras are disabled there.
 */
export const CAST_LEVELS_FOR_TIER: Readonly<Record<string, readonly CastLodLevel[]>> = {
  low: [3],
  medium: [2, 3],
  high: [1, 2, 3],
  ultra: [0, 1, 2, 3],
};

export function castLevelsForQuality(tier: string): readonly CastLodLevel[] {
  return CAST_LEVELS_FOR_TIER[tier] ?? CAST_LOD_LEVELS;
}
